import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import api from '~/api/Endpoints';
import type { InitialData } from '~/lib/getInitialData';
import isClient from '~/scripts/isClient';
import type { Suggestions } from '~/types';

interface RootDataContextType {
  rootData: InitialData | undefined;
  setRootData: (data: InitialData) => void;
  suggestions: Suggestions.Suggestion[];
  suggestionsUuidMap: Map<string, Suggestions.Suggestion>;
  categorySuggestionsMap: Map<number, Suggestions.Suggestion[]>;
  categoryIdMap: Map<number, Suggestions.Category>;
}

let suggestionsPromise: Promise<Suggestions.Suggestion[]> | undefined;
function fetchSuggestionsOnce(): Promise<Suggestions.Suggestion[]> {
  if (!suggestionsPromise) {
    suggestionsPromise = api
      .get('/suggestions')
      .then((result) => result as Suggestions.Suggestion[])
      .catch(() => [] as Suggestions.Suggestion[]);
  }
  return suggestionsPromise;
}

const RootDataContext = createContext<RootDataContextType | undefined>(
  undefined
);

interface RootDataProviderProps {
  children: ReactNode;
  initialData?: InitialData;
}

export function RootDataProvider({
  children,
  initialData,
}: RootDataProviderProps) {
  const [rootData, setRootData] = useState<InitialData | undefined>(
    initialData
  );

  // Update internal state when initialData prop changes (e.g. after revalidation)
  useEffect(() => {
    if (initialData) {
      setRootData(initialData);
      window.rootData = initialData;
    }
  }, [initialData]);

  const setRootDataCallback = useCallback((data: InitialData) => {
    setRootData(data);
    window.rootData = data;
  }, []);

  const [suggestions, setSuggestions] = useState<Suggestions.Suggestion[]>([]);

  useEffect(() => {
    if (!isClient()) {
      return;
    }
    let cancelled = false;
    fetchSuggestionsOnce().then((result) => {
      if (!cancelled) {
        setSuggestions(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const suggestionsUuidMap = useMemo(() => {
    const map = new Map<string, Suggestions.Suggestion>();
    for (const suggestion of suggestions) {
      map.set(suggestion.uuid, suggestion);
    }
    return map;
  }, [suggestions]);

  const categorySuggestionsMap = useMemo(() => {
    const map = new Map<number, Suggestions.Suggestion[]>();
    for (const suggestion of suggestions) {
      for (const categoryId of suggestion.categories_ids) {
        const existing = map.get(categoryId);
        if (existing) {
          existing.push(suggestion);
        } else {
          map.set(categoryId, [suggestion]);
        }
      }
    }
    return map;
  }, [suggestions]);

  const categoryIdMap = useMemo(() => {
    const map = new Map<number, Suggestions.Category>();
    for (const category of rootData?.categories || []) {
      map.set(category.id, category);
    }
    return map;
  }, [rootData?.categories]);

  const contextValue: RootDataContextType = {
    rootData,
    setRootData: setRootDataCallback,
    suggestions,
    suggestionsUuidMap,
    categorySuggestionsMap,
    categoryIdMap,
  };

  return (
    <RootDataContext.Provider value={contextValue}>
      {children}
    </RootDataContext.Provider>
  );
}

export function useRootDataContext(): RootDataContextType {
  const context = useContext(RootDataContext);
  if (context === undefined) {
    throw new Error(
      'useRootDataContext must be used within a RootDataProvider'
    );
  }
  return context;
}

// Hook that returns the root data, compatible with existing useRootData hook
export function useRootDataFromProvider(): InitialData | undefined {
  const { rootData } = useRootDataContext();
  return rootData;
}

export function useSuggestionsFromProvider(): Suggestions.Suggestion[] {
  const { suggestions } = useRootDataContext();
  return suggestions;
}

export function useSuggestionsUuidMap(): Map<string, Suggestions.Suggestion> {
  const { suggestionsUuidMap } = useRootDataContext();
  return suggestionsUuidMap;
}

export function useCategorySuggestionsMap(): Map<
  number,
  Suggestions.Suggestion[]
> {
  const { categorySuggestionsMap } = useRootDataContext();
  return categorySuggestionsMap;
}

export function useCategoryIdMap(): Map<number, Suggestions.Category> {
  const { categoryIdMap } = useRootDataContext();
  return categoryIdMap;
}
